import React, { useState, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, FolderPlus } from 'lucide-react';
import axios from 'axios';
import ImageUploader from '../../components/admin/ImageUploader';

const CATEGORIES = [
  { id: 'mariages', label: 'Mariages', label_en: 'Weddings' },
  { id: 'baby-shower', label: 'Baby Shower', label_en: 'Baby Shower' },
  { id: 'anniversaires', label: 'Anniversaires', label_en: 'Birthdays' },
  { id: 'corporatif', label: 'Événements Corporatifs', label_en: 'Corporate Events' },
  { id: 'halloween', label: 'Halloween', label_en: 'Halloween' },
  { id: 'noel', label: 'Noël', label_en: 'Christmas' },
  { id: 'autres', label: 'Autres Événements', label_en: 'Other Events' }
];

export default function GalleryManager() {
  const [photos, setPhotos] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('mariages');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotos(response.data.items || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const handleImageUpload = async (imageUrl) => {
    setUploading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('admin_token');
      const category = CATEGORIES.find(c => c.id === selectedCategory);
      
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery`,
        {
          image: imageUrl,
          category: selectedCategory,
          title: category.label,
          title_en: category.label_en,
          description: '',
          description_en: ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage('✅ Photo ajoutée avec succès!');
      fetchPhotos();
      
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Erreur lors de l\'ajout de la photo');
      console.error('Error adding photo:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Supprimer cette photo?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery/${photoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('✅ Photo supprimée');
      fetchPhotos();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Erreur lors de la suppression');
      console.error('Error deleting photo:', error);
    }
  };

  const photosByCategory = CATEGORIES.map(category => ({
    ...category,
    photos: photos.filter(p => p.category === category.id)
  }));

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Gestion de la Galerie</h1>

      {message && (
        <div className={`mb-4 p-4 rounded ${message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Upload className="w-5 h-5 mr-2" />
          Ajouter des Photos
        </h2>

        {/* Category Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Catégorie
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            {CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        {/* Image Upload */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Photo
          </label>
          <ImageUploader
            value=""
            onChange={handleImageUpload}
            label=""
          />
        </div>

        {uploading && (
          <div className="text-center text-gray-600">
            Ajout en cours...
          </div>
        )}
      </div>

      {/* Photos by Category */}
      <div className="space-y-8">
        {photosByCategory.map(category => (
          <div key={category.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center">
                <FolderPlus className="w-5 h-5 mr-2 text-purple-600" />
                {category.label}
              </h2>
              <span className="text-sm text-gray-500">
                {category.photos.length} photo{category.photos.length !== 1 ? 's' : ''}
              </span>
            </div>

            {category.photos.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucune photo dans cette catégorie</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {category.photos.map(photo => (
                  <div key={photo.id} className="relative group">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${photo.image}`}
                      alt={photo.title || 'Gallery photo'}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => handleDelete(photo.id)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
